import React from 'react';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import LiteralsService from 'src/services/literals/LiteralsService';
import MoreInfoTooltip from 'src/components/more-info-tooltip/MoreInfoTooltip';
import styles from './MonthlyAverageBox.module.scss';
import { DLSSize } from 'src/types/dls-size.enum';
import { trackClick } from 'src/hooks/useAdobeTags';
import { useLocation } from 'react-router-dom';
import SyncLoader from 'src/components/sync-loader/SyncLoader';
import { LoaderType } from 'src/types/loader-type.enum';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import Selectors from 'src/utils/selectors';
import PillLoader from '../../atoms/pillLoader/PillLoader';

export interface Props {
    amount: string;
    subtext: React.ReactElement<Typography>;
    id: string;
    onClick: () => void;
    className?: string;
    tagName?: string;
    linkText?: string;
    tooltipClassName?: string;
    loadingAmount?: boolean;
    href?: string;
    tag?: string;
    categoryName?: string;
}

export default function MonthlyAverageBox({
    amount,
    subtext,
    id,
    onClick,
    className,
    tagName,
    linkText,
    tooltipClassName,
    loadingAmount,
    href,
    tag,
    categoryName
}: Props): JSX.Element {
    const { pathname } = useLocation();
    return (
        <div className={`${styles.monthlyAverageBox} ${className}`}>
            <div className={styles.headerWithTooltip}>
                <Typography variant={TypographyVariants.SUBTEXT} component="p">
                    {LiteralsService.getLiteral('Budget.Create.monthlyAverage')}
                </Typography>
                <MoreInfoTooltip
                    body={LiteralsService.getLiteral('Budget.Create.monthlyAverageTooltip')}
                    className={tooltipClassName}
                    buttonAriaLabel={LiteralsService.getLiteral('Budget.Create.monthlyAverageTitle')}
                    id={id}
                    iconSize={DLSSize.MEDIUM}
                    onClick={() => trackClick(pathname, tagName)}
                />
            </div>
            {loadingAmount ? (
                <PillLoader />
            ) : (
                <SyncLoader type={LoaderType.PILL}>
                    <Typography
                        variant={TypographyVariants.HEADING_XS}
                        component="p"
                        className={amount === '$--' ? styles.emptyAmount : styles.defaultText}
                        data-cy={Selectors.SpendSmart.MONTHLY_AVERAGE_BOX}
                        aria-live="polite"
                        aria-busy={loadingAmount}
                    >
                        {amount}
                    </Typography>
                </SyncLoader>
            )}
            {subtext}
            {linkText && onClick && (
                <Anchor
                    onClick={onClick}
                    tag={tag}
                    data-cy={Selectors.SpendSmart.MONTHLY_AVERAGE_BOX_LINK}
                    href={href}
                    aria-label={
                        categoryName
                            ? LiteralsService.getLiteral('Budget.History.viewTransactionsLinkDescription', [
                                  categoryName
                              ])
                            : ''
                    }
                >
                    {linkText}
                </Anchor>
            )}
        </div>
    );
}
